import { gql, useMutation } from '@apollo/client';
import { AffiliationTypes, CustomFieldTypes } from '@generated/graphql';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { CustomFieldValueDisplay } from '@ovComponents/4-module/workflowCompletion/components/customFieldValueDisplay';
import { useConfirmationDialog } from '@ovComponents/4-module/workflowCompletion/components/useConfirmationDialog';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { reconcileWorkflowAccountContext } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { Box, Divider, Grid, IconButton, Typography } from 'hephaestus/web';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from 'hephaestus/webIcons';
import { groupBy } from 'lodash-es';
import {
  type ComponentProps,
  type ReactNode,
  useContext,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { ARCHIVE_GOAL } from './reviewAccount.queries';
import type { ReviewAccountSubstepOptions } from './reviewAccount.visual';

const DELETE_ACCOUNT = gql`
  mutation closeAccount($input: CloseAccountInput!) {
    closeAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const DELETE_SUB_ACCOUNT = gql`
  mutation closeSubAccount($input: CloseSubAccountInput!) {
    closeSubAccount(input: $input) {
      subAccount {
        id
      }
    }
  }
`;

const useAccountDeletion = (account: any, allAccounts: any[]) => {
  const [deleteAccountMutation] = useMutation(DELETE_ACCOUNT, {
    variables: {
      input: {
        accountId: account.id,
        inactiveReason: 'OTHER',
      },
    },
  });
  const { setWorkflowData, workflowData } = useContext(WorkflowContext);

  const [deleteSubAccountMutation] = useMutation(DELETE_SUB_ACCOUNT);
  const [archiveGoalMutation] = useMutation(ARCHIVE_GOAL);

  const deleteAccount = async () => {
    const goalIdsFromDeletedAccount = (account.subAccounts || [])
      .map((subAccount: any) => subAccount.goal?.id)
      .filter((id: any): id is string => !!id);

    const otherAccounts = allAccounts.filter((a) => a.id !== account.id);
    const goalIdsUsedByOtherAccounts = new Set(
      otherAccounts.flatMap((a) =>
        (a.subAccounts || [])
          .map((sa: any) => sa.goal?.id)
          .filter((id: any): id is string => !!id),
      ),
    );

    const orphanedGoalIds = goalIdsFromDeletedAccount.filter(
      (goalId: any) =>
        workflowData.createdGoalIds?.includes(goalId) &&
        !goalIdsUsedByOtherAccounts.has(goalId),
    );

    const subAccountResults = await Promise.allSettled(
      (account.subAccounts || []).map((subAccount: any) =>
        deleteSubAccountMutation({
          variables: {
            input: {
              subAccountId: subAccount.id,
              inactiveReason: 'OTHER',
            },
          },
        }).then((result) => {
          if (result.errors?.length) {
            throw new Error(result.errors[0].message);
          }
          return subAccount.id;
        }),
      ),
    );

    const successfullyDeletedSubAccountIds = subAccountResults
      .filter(
        (result): result is PromiseFulfilledResult<string> =>
          result.status === 'fulfilled',
      )
      .map((result) => result.value);

    if (successfullyDeletedSubAccountIds.length > 0) {
      await setWorkflowData((data) => ({
        ...data,
        currentSubAccountId: successfullyDeletedSubAccountIds.includes(
          data.currentSubAccountId ?? '',
        )
          ? undefined
          : data.currentSubAccountId,
        createdSubAccountIds: data.createdSubAccountIds?.filter(
          (id) => !successfullyDeletedSubAccountIds.includes(id),
        ),
      }));
    }

    await deleteAccountMutation();
    await setWorkflowData((data) => {
      const remainingAccounts =
        data.createdAccounts?.filter(({ id }) => id !== account.id) ?? [];

      return reconcileWorkflowAccountContext({
        ...data,
        createdAccounts: remainingAccounts,
        formMapping: data.formMapping?.filter(
          (fm) => fm.objectId != null && fm.objectId !== account.id,
        ),
      });
    });

    const archiveResults = await Promise.allSettled(
      orphanedGoalIds.map((goalId: any) =>
        archiveGoalMutation({
          variables: {
            input: {
              goalId,
            },
          },
        }).then((result) => {
          if (result.errors?.length) {
            throw new Error(result.errors[0].message);
          }
          return goalId;
        }),
      ),
    );

    const successfullyArchivedGoalIds = archiveResults
      .filter(
        (result): result is PromiseFulfilledResult<string> =>
          result.status === 'fulfilled',
      )
      .map((result) => result.value);

    if (successfullyArchivedGoalIds.length > 0) {
      await setWorkflowData((data) => ({
        ...data,
        currentGoalId: successfullyArchivedGoalIds.includes(
          data.currentGoalId ?? '',
        )
          ? undefined
          : data.currentGoalId,
        createdGoalIds: data.createdGoalIds?.filter(
          (id) => !successfullyArchivedGoalIds.includes(id),
        ),
      }));
    }
  };

  return { deleteAccount };
};

const FieldDisplay = ({
  title,
  children,
  expanded = true,
}: {
  title: string;
  children: ReactNode;
  expanded?: boolean;
}) => (
  <Grid
    container
    size={12}
    sx={{
      columnGap: 2,
    }}
  >
    <Grid size={expanded ? 12 : 3}>
      <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Grid>
    <Grid size={expanded ? 12 : 7}>
      <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
        {children}
      </Typography>
    </Grid>
  </Grid>
);

export const AccountCard = ({
  account,
  allAccounts,
  options,
  onAccountDeleted,
  onAccountEdit,
}: {
  account: any;
  allAccounts: any[];
  options: ReviewAccountSubstepOptions;
  onAccountDeleted: (accountId: string) => any;
  onAccountEdit: (accountId: string) => any;
}) => {
  const { translateBackendString } = useLocalization();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation([
    'workflowCompletions',
    'accountTypes',
    'affiliationTypes',
    'client',
  ]);

  const { confirmationDialog, getUserConfirmation } = useConfirmationDialog();

  const accountDisplayTitle = account.translatedNickName
    ? translateBackendString(account.translatedNickName)
    : t(`accountTypes:${account.type}`);

  const { deleteAccount } = useAccountDeletion(account, allAccounts);

  const onDeleteButtonClick: ComponentProps<typeof IconButton>['onClick'] =
    async (e) => {
      e.stopPropagation();
      const { closeDialog, confirmed } = await getUserConfirmation({
        title: t('reviewAccount.deleteAccountConfirmation.title', {
          accountDisplayTitle,
          accountId: `****${account.id.slice(-5)}`,
        }),
        bodyText: t('reviewAccount.deleteAccountConfirmation.bodyText', {
          accountDisplayTitle,
          accountId: `****${account.id.slice(-5)}`,
        }),
        setConfirmButtonColor: 'error',
      });
      if (!confirmed) {
        closeDialog();
        return;
      }
      await deleteAccount();
      await onAccountDeleted(account.id);
      closeDialog();
    };

  const onEditButtonClick: ComponentProps<typeof IconButton>['onClick'] =
    async (e) => {
      e.stopPropagation();
      await onAccountEdit(account.id);
    };

  const affiliatesByTypes = Object.entries(
    groupBy(account.affiliations || [], (a) => a.type),
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      variant='workflow'
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderTop: 'none !important',
          '.MuiAccordionSummary-content': {
            justifyContent: 'space-between !important',
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            position: 'absolute',
            top: '10px',
            right: '15px',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
              {accountDisplayTitle} ****{account.id.slice(-5)}
            </Typography>
            <Box
              sx={{
                mr: 3,
              }}
            >
              {options.enableAccountDelete && (
                <IconButton
                  aria-label='delete'
                  size='xsmall'
                  onClick={onDeleteButtonClick}
                >
                  <DeleteIcon sx={{ fontSize: '15px' }} fontSize='inherit' />
                </IconButton>
              )}
              {options.enableAccountEdit && (
                <IconButton
                  aria-label='edit'
                  size='xsmall'
                  onClick={onEditButtonClick}
                >
                  <EditIcon sx={{ fontSize: '15px' }} fontSize='inherit' />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Divider />
            {!expanded && (
              <Box
                sx={{
                  width: '100%',
                  mt: 2,
                  display: 'block',
                }}
              >
                <Grid
                  container
                  sx={{
                    rowGap: 2,
                  }}
                >
                  {options.displayAccountType && (
                    <FieldDisplay
                      expanded={false}
                      title={t('workflowCompletions:reviewAccount.accountType')}
                    >
                      {t(`accountTypes:${account.type}`)}
                    </FieldDisplay>
                  )}
                  {options.displayCustodian && (
                    <FieldDisplay
                      expanded={false}
                      title={t('workflowCompletions:reviewAccount.custodian')}
                    >
                      {account.custodianConnection?.name}
                    </FieldDisplay>
                  )}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid
          container
          sx={{
            rowGap: 2,
          }}
        >
          {!options.displayAccountType ? null : (
            <FieldDisplay
              title={t('workflowCompletions:reviewAccount.accountType')}
            >
              {t(`accountTypes:${account.type}`)}
            </FieldDisplay>
          )}
          {!options.displayCustodian ? null : (
            <FieldDisplay
              title={t('workflowCompletions:reviewAccount.custodian')}
            >
              {account.custodianConnection?.name}
            </FieldDisplay>
          )}
          {!(options.displayRepcode && account.repCode?.name) ? null : (
            <FieldDisplay
              title={t('workflowCompletions:reviewAccount.repCode')}
            >
              {account.repCode?.name}
            </FieldDisplay>
          )}
          {!(
            options.displayAffiliates && account.affiliations?.length
          ) ? null : (
            <>
              {affiliatesByTypes
                .filter(
                  ([affiliationType]) =>
                    affiliationType !== AffiliationTypes.Other,
                )
                .map(([affiliationType, affiliations]) => (
                  <FieldDisplay
                    title={t(`affiliationTypes:${affiliationType}`)}
                  >
                    {affiliations.map((a) => (
                      <Box>
                        {a.user.firstName} {a.user.lastName} (
                        {t(
                          `affiliationTypes:affiliationRelationship.${a.relation}`,
                        )}
                        )
                      </Box>
                    ))}
                  </FieldDisplay>
                ))}
              {affiliatesByTypes
                .filter(
                  ([affiliationType]) =>
                    affiliationType === AffiliationTypes.Other,
                )
                .map(([, affiliations]) => (
                  <FieldDisplay title={t('reviewAccount.otherAffiliates')}>
                    {affiliations.map((a) => (
                      <Box>
                        {a.user.firstName} {a.user.lastName} (
                        {t(
                          `affiliationTypes:affiliationRelationship.${a.relation}`,
                        )}
                        )
                      </Box>
                    ))}
                  </FieldDisplay>
                ))}
            </>
          )}
          {!(
            options.displayLinkedGoal && account.subAccounts?.length
          ) ? null : (
            <FieldDisplay title={t('reviewAccount.linkedGoals')}>
              {account.subAccounts?.map(({ goal }: any, index: number) => (
                <Box key={goal?.id ?? index}>
                  {goal?.name ?? t(`client:goalType.${goal?.type}`)}
                </Box>
              ))}
            </FieldDisplay>
          )}
          {!options.displayCustomFields
            ? null
            : account.customFields
                ?.filter(
                  (field: any) =>
                    field?.customField?.type !== CustomFieldTypes.CustomObject,
                ) /** @todo: remove this filter with https://onewealth.atlassian.net/browse/OV-19381 */
                ?.map(
                  (field: any) =>
                    field && (
                      <FieldDisplay
                        title={translateBackendString(
                          field.customField?.translatedName,
                        )}
                        key={field.key}
                      >
                        <CustomFieldValueDisplay field={field as any} />
                      </FieldDisplay>
                    ),
                )}
        </Grid>
      </AccordionDetails>
      {confirmationDialog}
    </Accordion>
  );
};
